<template>
  <div ref="element" class="head-canvas">
    <div class="canvas-wrapper" ref="canvasWrapper" :class="{ resizing: isResizing }"></div>
    <ThreeHeadTooltip
      v-if="graph"
      type="Solo"
      :graph="graph"
      :renderer="tooltipRenderer"
      :hovered-node="hoveredNode"
      :selected-node="selectedNode"
      :enabled="!!hoveredNode && !autoRotate"
    ></ThreeHeadTooltip>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, onBeforeUnmount, watch, watchEffect } from "vue";
import { debug as d } from "debug";
import { CompositeHeadApiModel } from "../../../api-models";
import ThreeHeadTooltip from "./three-head-tooltip.vue";
import { SoloOrViewpointsOption } from "./nodes-and-links-models";
import useNodesAndLinksCurrentData from "./use-nodes-and-links-data";
import useNodesAndLinksGraph from "./use-nodes-and-links-graph";
import { graphOptions } from "./3d-node-renderer";

const debug = d("cs.heatmaps");

const soloOrViewpoints = ref<SoloOrViewpointsOption>("solo");

const props = defineProps<{
  expanded: boolean;
  model: CompositeHeadApiModel | undefined;
}>();

const canvasWrapper = ref<HTMLElement>();
const element = ref<HTMLElement>();
const size = ref<{ width: number; height: number }>();
const isResizing = ref(false);

const currentParentNode = computed(() => {
  return props.expanded ? props.model!.rootNode : null;
});

const { data: heatmapData } = useNodesAndLinksCurrentData(props.model!, currentParentNode);
const { graph, selectedNode, hoveredNode, autoRotate, tooltipRenderer } = useNodesAndLinksGraph(
  heatmapData,
  canvasWrapper,
  size,
  isResizing,
  soloOrViewpoints,
);

function updateSize() {
  debug("updateSize");
  const { width, height } = element.value!.getBoundingClientRect();
  debug("new size", { width, height });
  size.value = { width, height };
  isResizing.value = false;
}

let resizeTimeout: any | null = null;

function onWindowResize() {
  debug("onWindowResize");
  isResizing.value = true;
  // Use a simple debounce to avoid repeated work during prolonged resize
  if (resizeTimeout) clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    updateSize();
  }, 500);
}

const resizeObserver = new ResizeObserver(updateSize);

onMounted(() => {
  window.addEventListener("resize", onWindowResize);
  resizeObserver.observe(element.value!);
  updateSize();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onWindowResize);
  resizeObserver.unobserve(element.value!);
});
</script>

<style lang="less" scoped>
.head-canvas {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.canvas-wrapper.resizing {
  // display: none;
}
</style>
